"use client"

import { useEffect, useRef, useState } from "react"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// Sources:
//   • USDA Food Safety and Inspection Service: refrigerator at or below 40°F,
//     freezer at or below 0°F. "Danger zone" 40–140°F where bacterial growth
//     accelerates.
//   • USDA Food Storage Chart and FoodSafety.gov shelf-life guidance.
//   • Manufacturer crisper-drawer guidance (Whirlpool, GE, LG, Samsung):
//     high-humidity drawers for leafy greens; low-humidity drawers for
//     ethylene-emitting fruits.
//   • USDA cross-contamination guidance: store raw meat on the lowest shelf
//     to prevent drips onto ready-to-eat foods.

const SECTIONS = [
  { id: "set-points", t: "Set points" },
  { id: "shelf-zones", t: "Shelf zones" },
  { id: "drawers", t: "Crisper drawers" },
  { id: "door", t: "Door bins" },
  { id: "freezer", t: "Freezer zones" },
  { id: "mistakes", t: "Common mistakes" },
] as const

export function ZoneTemperatureMappingPageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Refrigerator Temperature Zone Guide"
      heroEyebrow="Food Storage"
      heroTitlePre="Every food has a"
      heroTitleItalic="zone."
      heroTitlePost="Here's the map."
      heroSubtitle="USDA holds the fridge at or below 40°F and the freezer at or below 0°F — but inside that envelope, your shelves, drawers, and door bins all run a few degrees apart. This guide tells you what belongs where, and why."
      heroMeta={["⏱ ~6 min read", "USDA + manufacturer guidance"]}
      heroShare={<ToolShareBar title="Refrigerator Temperature Zone Guide" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            Quick answer
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Hold the fridge at <em className="italic text-[#ff9951]">37°F</em> and the freezer at{" "}
            <em className="italic text-[#ff9951]">0°F</em>. Raw meat goes on the lowest shelf;
            dairy on the middle; condiments in the door.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Fridge
              </div>
              <div className="text-[22px] font-black tracking-tight">≤ 40°F</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Freezer
              </div>
              <div className="text-[22px] font-black tracking-tight">≤ 0°F</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "What temperature should the fridge actually be set to?",
          answer:
            "USDA guidance is to hold the refrigerator at or below 40°F and the freezer at or below 0°F. Most manufacturers recommend a 37°F set point for the main compartment because it gives a 3°F buffer against door-opening warm-ups while staying well above the freezing risk for produce.",
        },
        {
          question: "Why shouldn't I store milk in the door?",
          answer:
            "The door is the warmest part of the cabinet — typically 38–42°F — and it sees the largest temperature swings every time you open the fridge. Milk, eggs, and other highly perishable dairy hold their shelf life best on the middle or upper main shelves, where the temperature is most stable.",
        },
        {
          question: "What's the difference between high- and low-humidity crisper drawers?",
          answer:
            "Manufacturer guidance: the high-humidity drawer (vent closed) traps moisture for leafy greens, broccoli, and herbs that wilt when dry. The low-humidity drawer (vent open) lets ethylene escape — important for apples, pears, and stone fruits that ripen and over-ripen on their own gas.",
        },
        {
          question: "Why does raw meat go on the bottom shelf?",
          answer:
            "USDA cross-contamination guidance: any drips from raw meat, poultry, or fish should fall onto a surface, never onto ready-to-eat food. The bottom shelf is also the coldest zone on most refrigerators, which holds those proteins below the danger zone (above 40°F) longer.",
        },
        {
          question: "How do French-door, side-by-side, and top-freezer zones differ?",
          answer:
            "The set points are the same, but airflow is not. Top-freezer units have the coldest air entering at the top of the main compartment, so the upper shelves run cooler. Side-by-sides have narrow vertical zones and the door runs warmer because the cold air falls. French-door designs include a dedicated deli/meat drawer that runs colder than the main shelves — the coldest non-freezer zone in the cabinet.",
        },
      ]}
      relatedTools={[
        {
          title: "Temperature Alarm Guide",
          category: "Temperature",
          description: "Why your fridge beeps and how to fix it.",
          href: "/tools/refrigerator-temperature-alarm-guide",
          time: "~6 min",
        },
        {
          title: "Temperature Settings Guide",
          category: "Temperature",
          description: "Best dial settings by season and household.",
          href: "/tools/refrigerator-temperature-settings-guide",
          time: "~5 min",
        },
        {
          title: "Food Storage Calculator",
          category: "Food Storage",
          description: "How long every food actually lasts.",
          href: "/tools/food-storage-calculator",
          time: "~30 sec",
        },
        {
          title: "Power Outage Food Safety",
          category: "Food Storage",
          description: "What to keep, what to toss after an outage.",
          href: "/tools/power-outage-food-safety-guide",
          time: "~4 min",
        },
      ]}
      cta={{
        title: "Crisper drawers always full? Door bins overflowing?",
        body: "A French-door refrigerator with a dedicated deli drawer and dual-humidity crispers gives every food its zone. Browse the full lineup with live pricing.",
        buttonLabel: "Browse French door refrigerators",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      {/* ============ ARTICLE BODY WITH STICKY TOC ============ */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* Article body */}
          <article>
            <GuideTool
              eyebrow="Interactive shelf picker"
              title="Where should this food live?"
              intro="Pick the food \u2014 get the zone, and why that spot."
              questions={[
                            {
                              id: "item",
                              label: "What are you putting away?",
                              options: [
                                { value: "meat", label: "Raw meat / poultry / fish" },
                                { value: "dairy", label: "Milk & dairy" },
                                { value: "leftovers", label: "Leftovers" },
                                { value: "produce", label: "Produce" },
                                { value: "condiments", label: "Condiments" },
                                { value: "deli", label: "Deli meat & cheese" }
                              ],
                            },
                            {
                              id: "style",
                              label: "Does your fridge have a deli drawer?",
                              options: [
                                { value: "deli", label: "Yes" },
                                { value: "nodeli", label: "No" }
                              ],
                            }
              ]}
              compute={(a) => {
                            const map = {
                              meat: { v: "Bottom shelf, in a tray", d: "The coldest shelf \u2014 and below everything else, so a drip can never cross-contaminate. Never the door, never above ready-to-eat food." },
                              dairy: { v: "Bottom or middle shelf, at the back", d: "Milk wants the coldest stable air, which is low and rear. The door is the warmest, swingiest zone in the box \u2014 milk there loses days of life." },
                              leftovers: { v: "Upper-middle shelf, front", d: "Eye-level and consistent \u2014 cool enough for safety, visible enough to actually get eaten before the USDA 3\u20134 day leftover window closes." },
                              produce: { v: "The crisper drawers", d: "Humidity control is the point: leafy greens in high humidity (vents closed), apples and pears in low (vents open) so their ethylene escapes instead of wilting the neighbors." },
                              condiments: { v: "The door", d: "The one category built for the warmest zone \u2014 high acid, high salt, high sugar tolerate the door's temperature swings that would age milk or eggs." },
                            }
                            if (a.item === "deli") {
                              return a.style === "deli"
                                ? { tone: "good", verdict: "The deli drawer", d: "", detail: "That shallow drawer runs a touch colder than the shelves \u2014 exactly the band sliced meat and cheese keep best in. Keep packs sealed; deli meat runs 3\u20135 days once opened." }
                                : { tone: "good", verdict: "Lowest shelf, front half", detail: "No deli drawer \u2014 use the coldest shelf but keep it in a sealed container in the front where you see it; opened deli meat has a 3\u20135 day window." }
                            }
                            const r = map[a.item]
                            return { tone: "good", verdict: r.v, detail: r.d }
                          }}
              ctaLabel="Place it"
              footnote="Zones follow the temperature map in the guide below (USDA windows)."
            />
            {/* Set points */}
            <section id="set-points" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                The set points USDA actually wants
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                USDA guidance is unambiguous: hold the refrigerator at or below{" "}
                <em className="font-semibold not-italic text-[#ff9951]">40°F</em> and the freezer at
                or below <em className="font-semibold not-italic text-[#ff9951]">0°F</em>. Above
                40°F, food enters the danger zone where bacterial growth accelerates rapidly.
              </p>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                In practice, target 37°F in the main compartment. That gives a 3°F buffer against
                door-opening warm-ups while staying comfortably above the 32°F freeze line for
                produce and condiments.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Verify with an independent thermometer
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    Built-in displays show the set point, not always the measured temperature.
                    Place a glass of water with a fridge thermometer inside; check after 24 hours.
                  </p>
                </div>
              </div>
            </section>

            {/* Shelf zones */}
            <section id="shelf-zones" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Shelf zones, top to bottom
              </h2>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Zone
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Typical temp
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Best for
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Avoid
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Upper shelves", "37–40°F", "Leftovers, drinks, ready-to-eat foods", "Raw meat (drip risk)"],
                      ["Middle shelves", "36–38°F", "Dairy, eggs, deli meats, opened cans", "Produce (use crisper)"],
                      ["Lower shelves", "34–37°F", "Raw meat, poultry, fish, marinating", "Produce (may freeze)"],
                      ["Deli / meat drawer", "32–34°F", "Cold cuts, cheese, bacon, hot dogs", "Produce, eggs (freeze risk)"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-700">{r[2]}</td>
                        <td className="px-5 py-4 text-gray-600">{r[3]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[14px] text-gray-600 leading-[1.6] mt-4">
                Per USDA cross-contamination guidance, raw meat always belongs on the lowest shelf
                — any drips fall onto the shelf, not onto ready-to-eat food below.
              </p>
            </section>

            {/* Drawers */}
            <section id="drawers" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Crisper drawers — humidity matters more than temperature
              </h2>
              <div className="space-y-3">
                {[
                  {
                    t: "High-humidity drawer (vent closed)",
                    d: "Traps moisture for items that wilt when dry. Manufacturer guidance: leafy greens, broccoli, cauliflower, carrots, celery, fresh herbs, cucumbers, peppers.",
                  },
                  {
                    t: "Low-humidity drawer (vent open)",
                    d: "Lets ethylene gas escape so ripening fruit doesn't over-ripen itself. Apples, pears, grapes, berries, citrus, melons, stone fruits.",
                  },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(255,153,81,0.04)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#ff9951" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Door */}
            <section id="door" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Door bins — the warmest, swingiest zone
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Door bins typically run 38–42°F and see the largest temperature swings every time
                the fridge opens. Reserve them for items that tolerate that variation.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Condiments (ketchup, mustard, mayo)",
                  "Salad dressings, pickles, olives",
                  "Jams and jellies",
                  "Soft drinks and juice",
                  "Butter (if used quickly)",
                  "Skip: milk, eggs, raw meat, anything highly perishable",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Freezer */}
            <section id="freezer" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Freezer zones — keep it at or below 0°F
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                USDA: hold the freezer at or below 0°F. Items stored colder than that stay safe
                indefinitely; the only thing that degrades is quality. Organize by frequency of
                use, not by temperature — within a freezer set to 0°F, all zones run cold enough.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Top: ice cream and frequently used items",
                  "Middle: meats and proteins (label and date)",
                  "Bottom: bulk and long-term storage",
                  "Door: small items, ice packs (warmest zone)",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Mistakes */}
            <section id="mistakes" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Six mistakes that cost you food
              </h2>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  { n: "01", t: "Storing milk in the door", d: "The door's warmer 38–42°F band and constant swings cut milk's safe window. Move it to the middle or upper shelf, toward the back." },
                  { n: "02", t: "Putting hot food directly inside", d: "Cool to room temperature first (USDA: within 2 hours). Hot food spikes the cabinet temperature and pushes neighboring items into the danger zone." },
                  { n: "03", t: "Overcrowding the shelves", d: "Cold air needs to circulate. Manufacturer guidance: keep the cabinet around 75% full for the best efficiency-to-airflow balance." },
                  { n: "04", t: "Mixing raw and cooked foods", d: "Raw proteins on the bottom, ready-to-eat foods above. Use sealed containers to prevent juice transfer." },
                  { n: "05", t: "Ignoring the crisper humidity slider", d: "Default high-humidity setting wilts fruits; default low-humidity wilts greens. Match the drawer to the produce inside it." },
                  { n: "06", t: "Skipping the thermometer check", d: "Verify with an independent thermometer placed in a glass of water — built-in displays show set points, not measured temps." },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </article>
        </div>
      </section>

      {/* ============ PRODUCT CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Better zones, better fridges"
          title="French door refrigerators with dedicated deli drawers"
          description="Multi-zone designs with dual-humidity crispers and a 32–34°F deli compartment."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>
    </ToolPageShell>
  )
}
